import { useState, useEffect } from "react";
import {
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  doc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import Modal from "react-modal";
import ProveedorTable from "./ProveedorTable";
import Nav from "./Nav";
import moment from "moment";
import Spinner from "./Spinner";
import Sidebar from "./Sidebar";
import { useNavContext } from "../context/NavContext";
import { useLocation } from "react-router-dom";

moment.locale("es");
Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Roboto, sans-serif",
    "::WebkitScrollbar": { width: "0px", background: "transparent" },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  label: {
    display: "block",
    width: "160px",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  input: {
    width: "100%",
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 101 },
};

const stepContainerStyle = {
  display: "flex",
  justifyContent: "left",
  marginLeft: "100px",
  gap: "50px",
  marginBottom: "20px",
  cursor: "pointer",
};

const stepStyle = (isActive) => ({
  padding: "10px 15px",
  borderBottom: isActive ? "4px solid #007bff" : "4px solid transparent",
  transition: "border-bottom 0.3s ease",
  fontWeight: isActive ? "bold" : "normal",
});

const ListaProveedores = () => {
  const location = useLocation();
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    empresa: "",
    rfc: "",
    regimenFiscal: "",
    moneda: "",
    telefono: "",
    correo: "",
    imagenURL: null,
    domicilio: "",
    numeroExt: "",
    numeroInt: "",
    colonia: "",
    codigoPostal: "",
    ciudad: "",
    estado: "",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [editingProveedorId, setEditingProveedorId] = useState(null); // <-- SIEMPRE docId real
  const [isLoading, setIsLoading] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [showNuevoButton] = useState(true);

  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  useEffect(() => {
    obtenerProveedores();
  }, []);

  const obtenerProveedores = async () => {
    const proveedoresSnapshot = await getDocs(collection(db, "proveedores"));
    const listaProveedores = proveedoresSnapshot.docs.map((d) => ({
      __docId: d.id, // id real del documento
      ...d.data(), // aquí puede venir un campo "id" humano (p.ej. "P-0037")
    }));
    setProveedores(listaProveedores);
    setIsLoading(false);
  };

  const searchProveedores = (term) => {
    const lowerSearchTerm = term?.toLowerCase() ?? "";
    return proveedores.filter((p) => {
      const empresa = String(p.empresa ?? "");
      const rfc = String(p.rfc ?? "");
      const regimenFiscal = String(p.regimenFiscal ?? "");
      return (
        empresa.toLowerCase().includes(lowerSearchTerm) ||
        rfc.toLowerCase().includes(lowerSearchTerm) ||
        regimenFiscal.toLowerCase().includes(lowerSearchTerm)
      );
    });
  };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    // manejo básico para file input (por si luego lo subes a storage)
    if (type === "file") {
      setFormData((prev) => ({ ...prev, imagen: files?.[0] ?? null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validación mínima
    const requiredFields = ["empresa"];
    for (let field of requiredFields) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: `El campo "${
            field === "empresa" ? "Nombre del Proveedor" : field
          }" es obligatorio!`,
        });
        return;
      }
    }

    try {
      const proveedorData = {
        ...formData,
        telefono: formData.telefono || "",
        correo: formData.correo || "",
        rfc: formData.rfc || "",
        regimenFiscal: formData.regimenFiscal || "",
      };

      if (editingProveedorId) {
        // actualizar usando el docId real
        await updateDoc(
          doc(db, "proveedores", editingProveedorId),
          proveedorData
        );
        Swal.fire({
          icon: "success",
          title: "¡Proveedor actualizado con éxito!",
          showConfirmButton: false,
          timer: 1000,
        });
      } else {
        await addDoc(collection(db, "proveedores"), proveedorData);
        Swal.fire({
          icon: "success",
          title: "¡Proveedor registrado con éxito!",
          showConfirmButton: false,
          timer: 1000,
        });
      }

      await obtenerProveedores();
      resetFormData();
      setEditingProveedorId(null);
      setStep(1);
      setModalIsOpen(false);
    } catch (error) {
      console.error("Error al guardar proveedor:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al guardar el proveedor. Intenta nuevamente.",
      });
    }
  };

  // Espera __docId (id real de firestore)
  const handleEditProveedor = (__docId) => {
    const p = proveedores.find((x) => x.__docId === __docId);
    if (!p) return;

    setFormData({
      empresa: p.empresa || "",
      rfc: p.rfc || "",
      regimenFiscal: p.regimenFiscal || "",
      moneda: p.moneda || "",
      telefono: p.telefono || "",
      correo: p.correo || "",
      imagenURL: p.imagenURL || null,
      domicilio: p.domicilio || "",
      numeroExt: p.numeroExt || "",
      numeroInt: p.numeroInt || "",
      colonia: p.colonia || "",
      codigoPostal: p.codigoPostal || "",
      ciudad: p.ciudad || "",
      estado: p.estado || "",
    });

    setEditingProveedorId(__docId); // guarda docId real
    setStep(1);
    setModalIsOpen(true);
  };

  // Espera __docId (id real)
  const handleDeleteProveedor = async (__docId) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás revertir esto!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, "proveedores", __docId));
          Swal.fire({
            icon: "success",
            title: "¡Proveedor eliminado con éxito!",
            showConfirmButton: false,
            timer: 1000,
          });
          obtenerProveedores();
        } catch (error) {
          console.error("Error al eliminar proveedor:", error);
        }
      }
    });
  };

  const resetFormData = () => {
    setFormData({
      empresa: "",
      rfc: "",
      regimenFiscal: "",
      moneda: "",
      telefono: "",
      correo: "",
      imagenURL: null,
      domicilio: "",
      numeroExt: "",
      numeroInt: "",
      colonia: "",
      codigoPostal: "",
      ciudad: "",
      estado: "",
    });
  };

  const handleOpenModal = () => {
    resetFormData();
    setEditingProveedorId(null);
    setModalIsOpen(true);
  };

  return (
    <>
      <Nav
        handleSearch={searchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar proveedores"
              value={searchTerm ?? ""}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="task-filters-right">
          <div
            className="view-buttons-container"
            style={{ position: "relative" }}
          >
            <button className="view-button">
              <i className="bi bi-three-dots-vertical"></i>
            </button>
            <button className="exportar-button">
              <i className="bi bi-cloud-arrow-down"></i> Exportar
            </button>
            {showNuevoButton && (
              <button className="new-task-button" onClick={handleOpenModal}>
                + Nuevo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="cotizaciones-table">
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          style={customStyles}
        >
          <div>
            <button
              className="cerrar-button"
              onClick={() => setModalIsOpen(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <Nav
            handleSearch={searchTerm}
            proximasAVencer={proximasAVencer}
            proximosEventos={proximosEventos}
          />
          <Sidebar />

          {step === 1 && (
            <>
              <h2 style={{ textAlign: "left", marginLeft: "100px" }}>
                {editingProveedorId
                  ? "Editar Proveedor"
                  : "Agregar nuevo proveedor"}
              </h2>
              <div style={stepContainerStyle}>
                <div style={stepStyle(step === 1)} onClick={() => setStep(1)}>
                  Info
                </div>
                <div style={stepStyle(step === 2)} onClick={() => setStep(2)}>
                  Dirección
                </div>
              </div>
              <form onSubmit={handleSubmit} className="client-form">
                <div className="form-row">
                  <label className="required" style={customStyles.label}>
                    Nombre del Proveedor*
                  </label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="empresa"
                    placeholder="Nombre del Proveedor"
                    value={formData.empresa}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>RFC</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="rfc"
                    placeholder="RFC"
                    value={formData.rfc}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Régimen Fiscal</label>
                  <select
                    style={customStyles.select}
                    id="regimen"
                    name="regimenFiscal"
                    value={formData.regimenFiscal}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Seleccione un régimen fiscal
                    </option>
                    <option value="601">
                      601 - General de Ley Personas Morales
                    </option>
                    <option value="612">
                      612 - Personas Físicas con Actividades Empresariales y
                      Profesionales
                    </option>
                    <option value="616">616 - Sin obligaciones fiscales</option>
                    <option value="626">
                      626 - Régimen Simplificado de Confianza (RESICO)
                    </option>
                  </select>

                  <label style={customStyles.label}>Moneda</label>
                  <select
                    style={customStyles.select}
                    id="moneda"
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Seleccione una moneda
                    </option>
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Teléfono</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="telefono"
                    placeholder="Teléfono"
                    value={formData.telefono}
                    onChange={handleChange}
                  />

                  <label style={customStyles.label}>Email</label>
                  <input
                    style={customStyles.input}
                    type="email"
                    name="correo"
                    placeholder="Correo Electrónico"
                    value={formData.correo}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Imagen</label>
                  <input
                    type="file"
                    name="imagen"
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      display: "block",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h2 style={{ textAlign: "left", marginLeft: "100px" }}>
                {editingProveedorId
                  ? "Editar dirección del proveedor"
                  : "Dirección del proveedor"}
              </h2>
              <div style={stepContainerStyle}>
                <div style={stepStyle(step === 1)} onClick={() => setStep(1)}>
                  Info
                </div>
                <div style={stepStyle(step === 2)} onClick={() => setStep(2)}>
                  Dirección
                </div>
              </div>
              <form onSubmit={handleSubmit} className="client-form">
                <div className="form-row">
                  <label style={customStyles.label}>Domicilio</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="domicilio"
                    placeholder="Domicilio"
                    value={formData.domicilio}
                    onChange={handleChange}
                  />

                  <label style={customStyles.label}>No. Exterior</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="numeroExt"
                    placeholder="No. Ext"
                    value={formData.numeroExt}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>No. Interior</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="numeroInt"
                    placeholder="No. Int"
                    value={formData.numeroInt}
                    onChange={handleChange}
                  />

                  <label style={customStyles.label}>Colonia</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="colonia"
                    placeholder="Colonia"
                    value={formData.colonia}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Código Postal</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="codigoPostal"
                    placeholder="C.P."
                    value={formData.codigoPostal}
                    onChange={handleChange}
                  />

                  <label style={customStyles.label}>Ciudad</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="ciudad"
                    placeholder="Ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Estado</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="estado"
                    placeholder="Estado"
                    value={formData.estado}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-toolbar">
                  <div className="leftBtn">
                    <button
                      id="guardar-enviar"
                      className="btn btn-primary"
                      type="submit"
                      onClick={handleSubmit}
                    >
                      {editingProveedorId ? "Actualizar" : "Guardar y enviar"}
                    </button>
                    <button
                      id="cancelar"
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => setModalIsOpen(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </Modal>

        {isLoading ? (
          <Spinner />
        ) : searchTerm !== "" && searchProveedores(searchTerm).length === 0 ? (
          <div className="no-resultados">
            <div
              className="mensaje-sin-resultados"
              style={{ marginTop: "30px" }}
            >
              <p style={{ fontSize: "14px" }}>
                No existe ningún proveedor que coincida con esta búsqueda.
              </p>
            </div>
            <div className="imagen-sin-resultados">
              <img
                src="/img/noSearch.svg"
                alt="Sin resultados"
                className="sin-resultados-imagen"
              />
            </div>
          </div>
        ) : (
          <ProveedorTable
            proveedores={
              searchTerm ? searchProveedores(searchTerm) : proveedores
            }
            // Asegúrate de pasar __docId desde la tabla
            onEditProveedor={handleEditProveedor}
            onDeleteProveedor={handleDeleteProveedor}
          />
        )}
      </div>
    </>
  );
};

export default ListaProveedores;
